import { ApiTransport } from "./config/apiTransport";
import {
  Subscription,
  UserSubscription,
  UserSubscriptionWithDetails,
  UpdateUserSubscriptionRequest,
  OrganizationSubscription,
  OrganizationSubscriptionWithDetails,
  CreateOrganizationSubscriptionRequest,
  UpdateOrganizationSubscriptionRequest,
  CreateUserSubscriptionWithPriceValidationRequest,
  CreateOrganizationSubscriptionWithPriceValidationRequest,
  PaginationParams,
  UpgradeOption,
} from "@root/components/subscription/types";

export class SubscriptionService {
  private readonly _apiTransport: ApiTransport;

  constructor(sessionCookie?: string) {
    this._apiTransport = new ApiTransport(sessionCookie);
  }

  // Get all subscriptions with pagination
  getAll = (page: number, limit: number, search?: string, status?: string) => {
    let url = `/api/v1/subscriptions?page=${page}&limit=${limit}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (status) url += `&status=${status}`;
    return this._apiTransport.get<{
      subscriptions: Subscription[];
      total: number;
    }>(url);
  };

  // Get subscription by ID
  getById = (id: string) =>
    this._apiTransport.get<Subscription>(`/api/v1/subscriptions/${id}`);

  // Cancel a subscription
  cancel = (id: string) =>
    this._apiTransport.post<void>(`/api/v1/subscriptions/${id}/cancel`);

  // Reactivate a subscription
  reactivate = (id: string) =>
    this._apiTransport.post<void>(`/api/v1/subscriptions/${id}/reactivate`);

  // ========== User Subscription Endpoints ==========

  /**
   * Get current active subscription for a specific user
   * @param userId - The user ID
   * @returns The active user subscription with plan details
   */
  getCurrentByUserId = async (userId: string) => {
    try {
      return await this._apiTransport.get<UserSubscription>(
        `/api/v1/user-subscriptions/user/${userId}/current`,
      );
    } catch (error) {
      // Return null for 404 (no active subscription)
      if ((error as any)?.response?.status === 404) {
        return { data: null };
      }
      console.error(
        `Error fetching current subscription for user ${userId}:`,
        error,
      );
      throw error;
    }
  };

  /**
   * Get current active subscription for a specific user with full details (features, planVersion, pricing)
   * This is the new enhanced endpoint that returns everything in one call
   * @param userId - The user ID
   * @returns The active user subscription with features, planVersion, and pricing
   */
  getCurrentByUserIdWithDetails = async (userId: string) => {
    try {
      return await this._apiTransport.get<UserSubscriptionWithDetails>(
        `/api/v1/user-subscriptions/user/${userId}/current`,
      );
    } catch (error) {
      // Return null for 404 (no active subscription)
      if ((error as any)?.response?.status === 404) {
        return { data: null };
      }
      console.error(
        `Error fetching current subscription with details for user ${userId}:`,
        error,
      );
      throw error;
    }
  };

  /**
   * Get subscription history for a specific user
   * @param userId - The user ID
   * @returns Array of user subscriptions with isActive flag
   */
  getHistoryByUserId = async (userId: string) => {
    try {
      return await this._apiTransport.get<UserSubscription[]>(
        `/api/v1/user-subscriptions/user/${userId}/history`,
      );
    } catch (error) {
      console.error(
        `Error fetching subscription history for user ${userId}:`,
        error,
      );
      throw error;
    }
  };

  /**
   * Get all subscriptions for a specific user
   * @param userId - The user ID
   * @returns Array of all user subscriptions
   */
  getAllByUserId = async (userId: string) => {
    try {
      return await this._apiTransport.get<UserSubscription[]>(
        `/api/v1/user-subscriptions/user/${userId}`,
      );
    } catch (error) {
      console.error(`Error fetching subscriptions for user ${userId}:`, error);
      throw error;
    }
  };

  /**
   * Get a specific user subscription by ID
   * @param id - The subscription ID
   * @returns The user subscription details
   */
  getUserSubscriptionById = async (id: number) => {
    try {
      return await this._apiTransport.get<UserSubscription>(
        `/api/v1/user-subscriptions/${id}`,
      );
    } catch (error) {
      console.error(`Error fetching user subscription ${id}:`, error);
      throw error;
    }
  };

  /**
   * Create a new user subscription (for upgrades/initial setup)
   * @param data - The subscription creation data
   * @returns The created subscription
   */
  createUserSubscription = async (data: any) => {
    try {
      return await this._apiTransport.post<UserSubscription>(
        "/api/v1/user-subscriptions",
        data,
      );
    } catch (error) {
      console.error("Error creating user subscription:", error);
      throw error;
    }
  };

  /**
   * Create a new user subscription with price validation
   * Validates the buying price against plan price and coupon discount
   * @param data - The subscription creation data
   * @returns The created subscription with computed prices
   */
  createUserSubscriptionWithPriceValidation = async (
    data: CreateUserSubscriptionWithPriceValidationRequest,
  ) => {
    try {
      return await this._apiTransport.post<{
        id: number;
        computedBasePrice: number;
        computedFinalPrice: number;
        discount: number;
        startDate: string;
        endDate: string;
      }>("/api/v1/user-subscriptions/purchase-or-upgrade", data);
    } catch (error) {
      console.error(
        "Error creating user subscription with price validation:",
        error,
      );
      throw error;
    }
  };

  /**
   * Get upgrade options for a user's subscription
   * @param userId - The user ID
   * @param scope - Optional scope filter ("same-plan" or "all")
   * @param priceType - Optional price type filter ("monthly" or "annual")
   * @returns Array of upgrade options with price deltas
   */
  getUserUpgradeOptions = async (
    userId: string,
    scope: "same-plan" | "all" = "same-plan",
    priceType: "monthly" | "annual" = "monthly",
  ) => {
    try {
      return await this._apiTransport.get<UpgradeOption[]>(
        `/api/v1/user-subscriptions/user/${userId}/upgrade-options?scope=${scope}&priceType=${priceType}`,
      );
    } catch (error) {
      console.error(
        `Error fetching upgrade options for user ${userId}:`,
        error,
      );
      throw error;
    }
  };

  /**
   * Get subscription history for a user
   * @param userId - The user ID
   * @returns Array of user's subscription history
   */
  getUserSubscriptionHistory = async (userId: string) => {
    try {
      return await this._apiTransport.get<UserSubscription[]>(
        `/api/v1/user-subscriptions/user/${userId}/history`,
      );
    } catch (error) {
      console.error(
        `Error fetching subscription history for user ${userId}:`,
        error,
      );
      throw error;
    }
  };

  /**
   * Update an existing user subscription
   * @param data - The subscription update data
   * @returns The updated subscription
   */
  updateUserSubscription = async (data: UpdateUserSubscriptionRequest) => {
    try {
      return await this._apiTransport.post<UserSubscription>(
        `/api/v1/user-subscriptions/purchase-or-upgrade`,
        data,
      );
    } catch (error) {
      console.error(`Error updating user subscription ${data.id}:`, error);
      throw error;
    }
  };

  /**
   * Toggle auto-renewal for a user subscription
   * @param id - The subscription ID
   * @param enableAutoRenewal - The auto-renewal state to set (true to enable, false to disable)
   * @returns The updated subscription
   */
  toggleUserSubscriptionAutoRenewal = async (
    id: number,
    enableAutoRenewal: boolean,
  ) => {
    try {
      return await this._apiTransport.patch<UserSubscription>(
        `/api/v1/user-subscriptions/${id}/toggle-auto-renewal`,
        { enableAutoRenewal },
      );
    } catch (error) {
      console.error(
        `Error toggling auto-renewal for user subscription ${id}:`,
        error,
      );
      throw error;
    }
  };

  /**
   * Cancel/delete a user subscription
   * @param id - The subscription ID
   */
  deleteUserSubscription = async (id: number) => {
    try {
      return await this._apiTransport.delete(
        `/api/v1/user-subscriptions/${id}`,
      );
    } catch (error) {
      console.error(`Error deleting user subscription ${id}:`, error);
      throw error;
    }
  };

  /**
   * Verify payment and create subscription from Stripe checkout session
   * @param sessionId - The Stripe checkout session ID
   * @param useVerifySession - If true, uses '/api/v1/payment/verify-session' endpoint, otherwise uses '/api/v1/payment/verify-payment'
   * @returns The created subscription or verification status
   */
  verifyPayment = async (
    sessionId: string,
    useVerifySession: boolean = false,
  ) => {
    try {
      // Conditional endpoint selection
      console.log(`Using verify-session: ${useVerifySession}`);
      const endpoint = useVerifySession
        ? `/api/v1/payment/verify-session?sessionId=${encodeURIComponent(sessionId)}`
        : `/api/v1/payment/verify-payment?sessionId=${encodeURIComponent(sessionId)}`;

      console.log(
        `Verifying payment using ${useVerifySession ? "verify-session" : "verify-payment"} endpoint`,
      );

      return await this._apiTransport.get<{
        subscriptionId?: string;
        status: string;
        customerId?: string;
        planId?: string;
        priceId?: string;
        amount?: number;
        currency?: string;
        billingPeriod?: string;
        currentPeriodStart?: string;
        currentPeriodEnd?: string;
        cancelAt?: string | null;
        cancelAtPeriodEnd?: boolean;
        userSubscriptionId?: number;
        id?: number; // Keep for backward compatibility
        message?: string;
        subscription?: UserSubscription | OrganizationSubscription;
      }>(endpoint);
    } catch (error) {
      console.log("Error verifying payment for session:", error);
      console.error(`Error verifying payment for session ${sessionId}:`, error);
      throw error;
    }
  };

  // ==================== Organization Subscription Methods ====================

  /**
   * Get current active organization subscription
   * @param organizationId - The organization ID
   * @returns The current active subscription or null
   */
  getCurrentByOrganizationId = async (organizationId: string) => {
    try {
      return await this._apiTransport.get<OrganizationSubscription>(
        `/api/v1/organization-subscriptions/organization/${organizationId}/current`,
      );
    } catch (error) {
      console.error(
        `Error fetching current subscription for organization ${organizationId}:`,
        error,
      );
      throw error;
    }
  };

  /**
   * Get current active organization subscription with full details (features, planVersion, pricing)
   * Attempts to use enhanced endpoint if available, falls back to basic endpoint
   * @param organizationId - The organization ID
   * @returns The active organization subscription with features, planVersion, and pricing
   */
  getCurrentByOrganizationIdWithDetails = async (organizationId: string) => {
    try {
      // Try to get enhanced response (same endpoint, but backend may return enhanced data)
      const response = await this._apiTransport.get<
        OrganizationSubscriptionWithDetails | OrganizationSubscription
      >(
        `/api/v1/organization-subscriptions/organization/${organizationId}/current`,
      );

      // Check if response has enhanced structure (features and pricing)
      // Note: planVersion is not included in organization response, only planVersionId
      if (
        response?.data &&
        "features" in response.data &&
        "pricing" in response.data
      ) {
        console.log("Using enhanced organization subscription response");
        return { data: response.data as OrganizationSubscriptionWithDetails };
      }

      // Fallback to basic structure (should not happen if backend is updated)
      console.warn(
        "Organization endpoint does not return enhanced structure, falling back to basic response",
      );
      return { data: (response?.data as OrganizationSubscription) || null };
    } catch (error) {
      // Return null for 404 (no active subscription)
      if ((error as any)?.response?.status === 404) {
        return { data: null };
      }
      console.error(
        `Error fetching current subscription with details for organization ${organizationId}:`,
        error,
      );
      throw error;
    }
  };

  /**
   * Get organization subscription history
   * @param organizationId - The organization ID
   * @param params - Pagination parameters
   * @returns List of organization subscriptions
   */
  getHistoryByOrganizationId = async (
    organizationId: string,
    params?: PaginationParams,
  ) => {
    try {
      const queryParams = params
        ? `?page=${params.page}&limit=${params.limit}`
        : "";
      return await this._apiTransport.get<OrganizationSubscription[]>(
        `/api/v1/organization-subscriptions/organization/${organizationId}/history${queryParams}`,
      );
    } catch (error) {
      console.error(
        `Error fetching subscription history for organization ${organizationId}:`,
        error,
      );
      throw error;
    }
  };

  /**
   * Get all organization subscriptions
   * @param organizationId - The organization ID
   * @returns All subscriptions for the organization
   */
  getAllByOrganizationId = async (organizationId: string) => {
    try {
      return await this._apiTransport.get<OrganizationSubscription[]>(
        `/api/v1/organization-subscriptions/organization/${organizationId}`,
      );
    } catch (error) {
      console.error(
        `Error fetching all subscriptions for organization ${organizationId}:`,
        error,
      );
      throw error;
    }
  };

  /**
   * Get a specific organization subscription by ID
   * @param id - The subscription ID
   * @returns The organization subscription
   */
  getOrganizationSubscriptionById = async (id: number) => {
    try {
      return await this._apiTransport.get<OrganizationSubscription>(
        `/api/v1/organization-subscriptions/${id}`,
      );
    } catch (error) {
      console.error(`Error fetching organization subscription ${id}:`, error);
      throw error;
    }
  };

  /**
   * Create a new organization subscription
   * @param data - The subscription creation data
   * @returns The created subscription
   */
  createOrganizationSubscription = async (
    data: CreateOrganizationSubscriptionRequest,
  ) => {
    try {
      return await this._apiTransport.post<OrganizationSubscription>(
        "/api/v1/organization-subscriptions/purchase-or-upgrade",
        data,
      );
    } catch (error) {
      console.error("Error creating organization subscription:", error);
      throw error;
    }
  };

  /**
   * Create a new organization subscription with price validation
   * Validates the buying price against plan price and coupon discount
   * @param data - The subscription creation data
   * @returns The created subscription with computed prices
   */
  createOrganizationSubscriptionWithPriceValidation = async (
    data: CreateOrganizationSubscriptionWithPriceValidationRequest,
  ) => {
    try {
      return await this._apiTransport.post<{
        id: number;
        computedBasePrice: number;
        computedFinalPrice: number;
        discount: number;
        startDate: string;
        endDate: string;
      }>("/api/v1/organization-subscriptions/purchase-or-upgrade", data);
    } catch (error) {
      console.error(
        "Error creating organization subscription with price validation:",
        error,
      );
      throw error;
    }
  };

  /**
   * Get upgrade options for an organization's subscription
   * @param organizationId - The organization ID
   * @param scope - Optional scope filter ("same-plan" or "all")
   * @param priceType - Optional price type filter ("monthly" or "annual")
   * @returns Array of upgrade options with price deltas
   */
  getOrganizationUpgradeOptions = async (
    organizationId: string,
    scope: "same-plan" | "all" = "same-plan",
    priceType: "monthly" | "annual" = "monthly",
  ) => {
    try {
      return await this._apiTransport.get<UpgradeOption[]>(
        `/api/v1/organization-subscriptions/organization/${organizationId}/upgrade-options?scope=${scope}&priceType=${priceType}`,
      );
    } catch (error) {
      console.error(
        `Error fetching upgrade options for organization ${organizationId}:`,
        error,
      );
      throw error;
    }
  };

  /**
   * Get subscription history for an organization
   * @param organizationId - The organization ID
   * @returns Array of organization's subscription history
   */
  getOrganizationSubscriptionHistory = async (organizationId: string) => {
    try {
      return await this._apiTransport.get<OrganizationSubscription[]>(
        `/api/v1/organization-subscriptions/organization/${organizationId}/history`,
      );
    } catch (error) {
      console.error(
        `Error fetching subscription history for organization ${organizationId}:`,
        error,
      );
      throw error;
    }
  };

  /**
   * Update an existing organization subscription
   * @param data - The subscription update data
   * @returns The updated subscription
   */
  updateOrganizationSubscription = async (
    data: UpdateOrganizationSubscriptionRequest,
  ) => {
    try {
      return await this._apiTransport.post<OrganizationSubscription>(
        `/api/v1/organization-subscriptions/purchase-or-upgrade`,
        data,
      );
    } catch (error) {
      console.error(
        `Error updating organization subscription ${data.id}:`,
        error,
      );
      throw error;
    }
  };

  /**
   * Toggle auto-renewal for an organization subscription
   * @param id - The subscription ID
   * @param enableAutoRenewal - The auto-renewal state to set (true to enable, false to disable)
   * @returns The updated subscription
   */
  toggleOrganizationSubscriptionAutoRenewal = async (
    id: number,
    enableAutoRenewal: boolean,
  ) => {
    try {
      return await this._apiTransport.patch<OrganizationSubscription>(
        `/api/v1/organization-subscriptions/${id}/toggle-auto-renewal`,
        { enableAutoRenewal },
      );
    } catch (error) {
      console.error(
        `Error toggling auto-renewal for organization subscription ${id}:`,
        error,
      );
      throw error;
    }
  };

  /**
   * Cancel/delete an organization subscription
   * @param id - The subscription ID
   */
  deleteOrganizationSubscription = async (id: number) => {
    try {
      return await this._apiTransport.delete(
        `/api/v1/organization-subscriptions/${id}`,
      );
    } catch (error) {
      console.error(`Error deleting organization subscription ${id}:`, error);
      throw error;
    }
  };
}
// Export a single instance
export const subscriptionService = new SubscriptionService();
